import React from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Facebook, Instagram, Linkedin, Twitter, ArrowUp } from 'lucide-react';

const Footer = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const services = [
    'LED Sign Boards',
    'Acrylic Signage',
    '3D Letter Boards',
    'Retail Signage',
    'Petrol Pump Signage',
    'Corporate Branding',
    'Hoardings & Displays',
  ];

  const locations = [
    'Ahmedabad',
    'SG Highway',
    'Satellite',
    'Bopal',
    'Vastrapur',
    'Gandhinagar',
    'Vadodara',
    'Surat',
    'Gujarat',
  ];

  const quickLinks = [
    { name: 'Home', href: '#home' },
    { name: 'Services', href: '#services' },
    { name: 'Portfolio', href: '#portfolio' },
    { name: 'About Us', href: '#why-choose-us' },
    { name: 'Testimonials', href: '#testimonials' },
    { name: 'Contact', href: '#contact' },
  ];

  return (
    <footer className="bg-slate-900 relative overflow-hidden" data-testid="footer">
      {/* Watermark */}
      <div className="absolute inset-0 flex items-center justify-center overflow-hidden pointer-events-none">
        <span className="text-[20vw] font-['Oswald'] font-bold text-white/[0.02] whitespace-nowrap select-none">
          ASCENT
        </span>
      </div>

      {/* Main Footer */}
      <div className="relative z-10 py-20 border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            {/* Company Info */}
            <div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
              >
                <a href="#home" className="inline-block mb-6">
                  <span className="text-3xl font-['Oswald'] font-bold text-white">
                    ASCENT
                  </span>
                  <span className="block text-xs text-sky-400 tracking-[0.2em] uppercase font-['Manrope']">
                    International Inc.
                  </span>
                </a>
                <p className="text-slate-400 font-['Manrope'] text-sm leading-relaxed mb-4">
                  Transforming Brands Through Premium Signage Solutions. 
                  Leading LED sign board manufacturer in Ahmedabad with 15+ years of excellence.
                </p>
                <p className="text-slate-400 font-['Manrope'] text-sm mb-6">
                  <strong className="text-white">Contact:</strong> Mukul Sangani
                </p>
                
                {/* Social Links */}
                <div className="flex gap-3">
                  {[
                    { icon: Facebook, href: '#' },
                    { icon: Instagram, href: '#' },
                    { icon: Linkedin, href: '#' },
                    { icon: Twitter, href: '#' },
                  ].map((social, index) => {
                    const Icon = social.icon;
                    return (
                      <a
                        key={index}
                        href={social.href}
                        className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center hover:bg-sky-500 border border-slate-700 hover:border-sky-500 transition-all duration-300"
                        aria-label={`Follow us on ${Icon.name}`}
                      >
                        <Icon className="w-4 h-4 text-slate-400 hover:text-white" />
                      </a>
                    );
                  })}
                </div>
              </motion.div>
            </div>

            {/* Services */}
            <div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                viewport={{ once: true }}
              >
                <h4 className="text-lg font-['Oswald'] font-bold text-white uppercase mb-6">
                  Our Services
                </h4>
                <ul className="space-y-3">
                  {services.map((service, index) => (
                    <li key={index}>
                      <a
                        href="#services"
                        className="text-slate-400 font-['Manrope'] text-sm hover:text-sky-400 transition-colors duration-300"
                      >
                        {service}
                      </a>
                    </li>
                  ))}
                </ul>
              </motion.div>
            </div>

            {/* Service Areas */}
            <div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                viewport={{ once: true }}
              >
                <h4 className="text-lg font-['Oswald'] font-bold text-white uppercase mb-6">
                  Service Areas
                </h4>
                <div className="flex flex-wrap gap-2">
                  {locations.map((location, index) => (
                    <span
                      key={index}
                      className="text-xs px-3 py-1.5 bg-slate-800 border border-slate-700 rounded-full text-slate-400 font-['Manrope']"
                    >
                      {location}
                    </span>
                  ))}
                </div>
              </motion.div>
            </div>

            {/* Contact Info */}
            <div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                viewport={{ once: true }}
              >
                <h4 className="text-lg font-['Oswald'] font-bold text-white uppercase mb-6">
                  Contact Us
                </h4>
                <ul className="space-y-4">
                  <li>
                    <a
                      href="tel:+919825218789"
                      className="flex items-start gap-3 text-slate-400 font-['Manrope'] text-sm hover:text-sky-400 transition-colors duration-300"
                    >
                      <Phone className="w-4 h-4 mt-0.5 text-sky-400" />
                      +91 98252 18789
                    </a>
                  </li>
                  <li>
                    <a
                      href="tel:+917972431220"
                      className="flex items-start gap-3 text-slate-400 font-['Manrope'] text-sm hover:text-sky-400 transition-colors duration-300"
                    >
                      <Phone className="w-4 h-4 mt-0.5 text-sky-400" />
                      +91 79724 31220
                    </a>
                  </li>
                  <li>
                    <a
                      href="mailto:ascentin@gmail.com"
                      className="flex items-start gap-3 text-slate-400 font-['Manrope'] text-sm hover:text-sky-400 transition-colors duration-300"
                    >
                      <Mail className="w-4 h-4 mt-0.5 text-sky-400" />
                      ascentin@gmail.com
                    </a>
                  </li>
                  <li className="flex items-start gap-3 text-slate-400 font-['Manrope'] text-sm">
                    <MapPin className="w-4 h-4 mt-0.5 text-sky-400 flex-shrink-0" />
                    <span>
                      Sheet No 42, Besides Suyog 2, Plot No 213, Butbhavani Mandir Road, 
                      Vejalpur, Ahmedabad – 380051
                    </span>
                  </li>
                </ul>
              </motion.div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="relative z-10 py-6 border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-slate-500 font-['Manrope'] text-xs text-center md:text-left">
              © {new Date().getFullYear()} ASCENT INTERNATIONAL INC. All rights reserved. 
              <span className="block md:inline md:ml-1">
                LED Sign Board Manufacturer in Ahmedabad | Signage Company Gujarat
              </span>
            </p>

            {/* Quick Links */}
            <div className="flex flex-wrap items-center justify-center gap-4">
              {quickLinks.slice(0, 4).map((link, index) => (
                <a
                  key={index}
                  href={link.href}
                  onClick={(e) => {
                    e.preventDefault();
                    document.querySelector(link.href)?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="text-slate-500 font-['Manrope'] text-xs hover:text-sky-400 transition-colors duration-300"
                >
                  {link.name}
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Scroll to Top Button */}
      <button
        onClick={scrollToTop}
        className="fixed bottom-24 right-24 w-12 h-12 bg-white border border-gray-200 rounded-full flex items-center justify-center hover:bg-sky-50 hover:border-sky-300 transition-all duration-300 z-[60] shadow-lg"
        aria-label="Scroll to top"
        data-testid="scroll-to-top"
      >
        <ArrowUp className="w-5 h-5 text-slate-700" />
      </button>
    </footer>
  );
};

export default Footer;
